import { Box } from '@nimbus-ds/components';
import { AiFeature } from '@tiendanube/common/src/enums';
import FeatureFlag from 'App/components/FeatureFlag';
import { FEATURE_AI_TIER } from 'App/features';
import {
  AI_PRODUCT_SEO_BRAND_TAGS_ENABLED,
  CATALOG_PRODUCT_HASHED_SLUG,
} from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import TabsMultiLanguage from 'commons/components/TabsMultiLanguage';
import { useModal, useTranslationCommon } from 'commons/hooks';
import { useAiUsage } from 'domains/Ai/hooks';
import { useGetAllLanguages } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { AIGeneratedType } from 'domains/Catalog/Products/productsSlice';
import { hasSeoForAllLanguages } from 'domains/Catalog/Products/utils';
import FormSeo from './FormSeo';
import { FormSeoProps } from './FormSeo/FormSeo';
import TagAndSeoItemTitle from '../TagAndSeoItemTitle';

export interface SeoProps
  extends Omit<
    FormSeoProps,
    'showAiModal' | 'openAiModal' | 'closeAiModal' | 'showGenerateWithAi'
  > {
  missingNameAndDescription: boolean;
  onClickGenerateWithAI: (type: AIGeneratedType) => void;
}

function Seo(props: Readonly<SeoProps>): JSX.Element {
  const t = useTranslationCatalog();
  const tCommon = useTranslationCommon();
  const allLanguages = useGetAllLanguages();
  const [showAiModal, openAiModal, closeAiModal] = useModal();
  const { isLoadingAiUses, requiredTier, aiUsesUpsellTitle } = useAiUsage();

  const [hasCatalogProductHashedSlug] = useHasTags([
    CATALOG_PRODUCT_HASHED_SLUG,
  ]);

  const marginTop = hasCatalogProductHashedSlug ? '2' : 'none';

  const handleOpenSeoGenerationModal = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_PRODUCTS_SEO,
    callback: openAiModal,
    minValue: requiredTier,
    asAside: !hasCatalogProductHashedSlug,
  });

  const upsellSeo = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_PRODUCTS_SEO,
    callback: () => props.onClickGenerateWithAI('seo'),
    minValue: requiredTier,
    asAside: !hasCatalogProductHashedSlug,
  });

  const getTooltipText = () => {
    if (
      hasSeoForAllLanguages(allLanguages, props.seoTitle, props.seoDescription)
    )
      return tCommon('aiGeneration.alreadyExists.seo');
    if (props.missingNameAndDescription)
      return tCommon('aiGeneration.addNameAndDescriptionToGenerate');
    return '';
  };
  const tooltipText = getTooltipText();

  const generateWithAiLink = (
    <FeatureFlag flag={AI_PRODUCT_SEO_BRAND_TAGS_ENABLED}>
      <GenerateWithAiLink
        onClick={upsellSeo}
        trackingFeature={AiFeature.AI_PRODUCTS_SEO}
        fontSize="caption"
        disabled={!!tooltipText}
        tooltipText={tooltipText}
      />
    </FeatureFlag>
  );

  return (
    <TagAndSeoItemTitle
      title={t('products.detail.seo.title')}
      slot={generateWithAiLink}
      description={t('products.detail.seo.description')}
    >
      <Box marginTop={marginTop}>
        <TabsMultiLanguage>
          <FormSeo
            {...props}
            showAiModal={showAiModal}
            openAiModal={handleOpenSeoGenerationModal}
            closeAiModal={closeAiModal}
            showGenerateWithAi={!isLoadingAiUses}
          />
        </TabsMultiLanguage>
      </Box>
    </TagAndSeoItemTitle>
  );
}

export default Seo;
